import { OUTSIDE_FRAME_ZONE } from '@/constants/outsideFrameZone'

/**
 * Direction angles for cone-based candidate finding (radians)
 * math convention: 0=right, PI/2=down, PI=left, -PI/2=up
 * Note: screen Y increases downward
 */
export const DIRECTION_ANGLES: Record<'north' | 'south' | 'east' | 'west', number> = {
  east: 0,
  south: Math.PI / 2,
  west: Math.PI,
  north: -Math.PI / 2,
}

/** Half-width of the acceptance cone in radians (~45 degrees) */
export const CONE_HALF_ANGLE = Math.PI / 4

/**
 * Normalize angle to [-PI, PI]
 */
export function normalizeAngle(angle: number): number {
  while (angle > Math.PI) angle -= 2 * Math.PI
  while (angle < -Math.PI) angle += 2 * Math.PI
  return angle
}

/**
 * Angular cone-based candidate finder
 * Accepts any frame within ±45° of the target direction and within the distance band
 */
export function findCandidatesInZone(
  direction: 'north' | 'south' | 'east' | 'west',
  currentFrame: { frameIndex: number; worldX: number; worldY: number },
  frameWorldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }>,
  halfWidth: number,
  halfHeight: number,
  minDist: number,
  maxDist: number,
  _perpAlign: number // kept for signature compatibility, not used
): Array<{ frameIndex: number; distance: number; dx: number; dy: number }> {
  const candidates: Array<{
    frameIndex: number
    distance: number
    dx: number
    dy: number
  }> = []

  const targetAngle = DIRECTION_ANGLES[direction]
  // Use average of half-dimensions for distance scaling
  const halfDim = (halfWidth + halfHeight) / 2
  const minDistance = halfDim * minDist
  const maxDistance = halfDim * maxDist

  for (const candidate of frameWorldPositions) {
    if (candidate.frameIndex === currentFrame.frameIndex) continue

    const dx = candidate.worldX - currentFrame.worldX
    const dy = candidate.worldY - currentFrame.worldY
    const distance = Math.sqrt(dx * dx + dy * dy)

    // Distance band check
    if (distance < minDistance || distance > maxDistance) continue

    // Angular cone check: is candidate within ±CONE_HALF_ANGLE of target direction?
    const candidateAngle = Math.atan2(dy, dx)
    const angleDiff = Math.abs(normalizeAngle(candidateAngle - targetAngle))

    if (angleDiff <= CONE_HALF_ANGLE) {
      candidates.push({
        frameIndex: candidate.frameIndex,
        distance,
        dx,
        dy
      })
    }
  }

  return candidates
}

/**
 * Query frames outside current frame boundary using cone-based search.
 * Returns candidate frame indices and the nearest/best candidate.
 */
export function queryOutsideFrameCandidate(
  direction: 'north' | 'south' | 'east' | 'west',
  currentFrameIndex: number | null,
  frameWorldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }>,
  imageDimensions: { imageWidth: number; imageHeight: number; displayWidth: number; displayHeight: number } | null
): { candidateFrames: number[]; nearest: number | null } {
  if (
    currentFrameIndex === null ||
    !imageDimensions ||
    frameWorldPositions.length === 0
  ) {
    return { candidateFrames: [], nearest: null }
  }

  const currentFrame = frameWorldPositions[currentFrameIndex]
  if (!currentFrame) {
    return { candidateFrames: [], nearest: null }
  }

  const halfWidth = imageDimensions.displayWidth / 2
  const halfHeight = imageDimensions.displayHeight / 2
  const minDist = OUTSIDE_FRAME_ZONE.minDistancePercent
  const maxDist = OUTSIDE_FRAME_ZONE.maxDistancePercent

  // Cone-based search: single pass with ±45° cone, no progressive expansion needed
  const candidates = findCandidatesInZone(
    direction,
    currentFrame,
    frameWorldPositions,
    halfWidth,
    halfHeight,
    minDist,
    maxDist,
    0 // perpAlign unused in cone mode
  )

  // Selection logic with sequential proximity bonus:
  // Prefer frames that are both spatially close AND sequentially near.
  // First try candidates within MAX_SEQUENTIAL_JUMP steps; only if none exist
  // fall back to the full set (preserves inter-aisle navigation when needed).
  const MAX_SEQUENTIAL_JUMP = 20
  let selectedFrame: number | null = null
  
  if (candidates.length === 1) {
    // Single candidate: auto-select
    selectedFrame = candidates[0].frameIndex
  } else if (candidates.length > 1) {
    const SEQUENCE_WEIGHT = 10

    // Prefer nearby sequential candidates; fall back to all if none within range
    const nearbyCandidates = candidates.filter(
      c => Math.abs(c.frameIndex - currentFrameIndex) <= MAX_SEQUENTIAL_JUMP
    )
    const scoringCandidates = nearbyCandidates.length > 0 ? nearbyCandidates : candidates

    const bestCandidate = scoringCandidates.reduce((best, curr) => {
      const bestSequentialDist = Math.abs(best.frameIndex - currentFrameIndex)
      const currSequentialDist = Math.abs(curr.frameIndex - currentFrameIndex)
      
      const bestScore = best.distance + (bestSequentialDist * SEQUENCE_WEIGHT)
      const currScore = curr.distance + (currSequentialDist * SEQUENCE_WEIGHT)
      
      return currScore < bestScore ? curr : best
    })
    
    selectedFrame = bestCandidate.frameIndex
  }

  // Debug logging (reduced verbosity)
  if (candidates.length > 0) {
    console.log(`🎯 [OutsideFrameQuery] ${direction}: ${candidates.length} candidates, selected frame ${selectedFrame}`)
  }

  return {
    candidateFrames: candidates.map(c => c.frameIndex),
    nearest: selectedFrame
  }
}

/**
 * Build an NSEW walkthrough path from a starting frame.
 * Tries both east and west, picks the longer path.
 * Returns an array of frame indices representing the walkthrough.
 */
export function buildNSEWWalkthroughPath(
  startFrame: number,
  frameWorldPositions: Array<{ frameIndex: number; worldX: number; worldY: number }>,
  imageDimensions: { imageWidth: number; imageHeight: number; displayWidth: number; displayHeight: number },
  maxSteps: number = 60
): number[] {
  const directions: ('east' | 'west')[] = ['east', 'west']
  let bestPath: number[] = []

  for (const direction of directions) {
    const path = [startFrame]
    const visited = new Set<number>([startFrame])
    let currentFrame = startFrame

    for (let step = 0; step < maxSteps; step++) {
      const result = queryOutsideFrameCandidate(
        direction,
        currentFrame,
        frameWorldPositions,
        imageDimensions
      )

      if (result.nearest === null || visited.has(result.nearest)) {
        break
      }

      path.push(result.nearest)
      visited.add(result.nearest)
      currentFrame = result.nearest
    }

    if (path.length > bestPath.length) {
      bestPath = path
    }
  }

  console.log(`🧭 [buildNSEWWalkthroughPath] Best path: ${bestPath.length} steps from frame ${startFrame}`)
  return bestPath
}
